import {
  Flex,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Box,
} from "@chakra-ui/react";
import { TriangleAlert } from "lucide-react";
import { useCallback } from "react";

import {
  advanceTimeSliderMarks,
  timeRangeSliderMarks,
  LEGACY_MAX_ADVANCE_TIME_FALLBACK,
} from "../constants/SendConfigConstants";
import { SendConfigProps } from "../types/ISendConfig";
import { useTimeRangeSlider, useAdvanceTimeSlider } from "../hooks";
import {
  SliderHeader,
  ConfigRangeSlider,
  MinimumEventDurationSelect,
  ReceiptTypeCheckboxes,
  SEND_CONFIG_THEME,
} from "./SendConfig/index";

export default function SendConfig({
  t,
  handleChange,
  values,
  defaultOpen = false,
}: SendConfigProps) {
  const onTimeRangeChange = useCallback(
    (startTime: string, endTime: string) => {
      handleChange({
        target: {
          name: "startTime",
          value: startTime,
        },
      });
      handleChange({
        target: {
          name: "endTime",
          value: endTime,
        },
      });
    },
    [handleChange],
  );

  const onAdvanceTimeChange = useCallback(
    (minAdvanceTime: number, maxAdvanceTime: number) => {
      handleChange({
        target: {
          name: "minAdvanceTime",
          value: minAdvanceTime,
        },
      });
      handleChange({
        target: {
          name: "maxAdvanceTime",
          value: maxAdvanceTime,
        },
      });
    },
    [handleChange],
  );

  const {
    timeRange,
    handleTimeRangeChange,
    handleTimeRangeChangeEnd,
    formatTimeLabel,
  } = useTimeRangeSlider({
    startTime: values?.startTime,
    endTime: values?.endTime,
    onTimeRangeChange,
  });

  const {
    advanceTimeRange,
    sliderIndexRange,
    handleAdvanceTimeChange,
    handleAdvanceTimeChangeEnd,
    formatAdvanceTimeLabel,
    minIndex,
    maxIndex,
  } = useAdvanceTimeSlider({
    minAdvanceTime: values?.minAdvanceTime,
    maxAdvanceTime: values?.maxAdvanceTime,
    onAdvanceTimeChange,
  });

  return (
    <Accordion
      color="black"
      bg="white"
      mb={4}
      py={2}
      borderWidth={"1px"}
      borderColor={"gray.200"}
      borderRadius={"md"}
      allowToggle
      defaultIndex={defaultOpen ? 0 : -1}
    >
      <AccordionItem border={0}>
        <AccordionButton
          _focus={{
            border: 0,
          }}
          _hover={{
            backgroundColor: "none",
          }}
        >
          <Flex flex={1} gap="12px">
            <Text fontSize="md" color="#086AA9">
              {t("sendConfig.title")}
            </Text>
          </Flex>
          <AccordionIcon color="#086AA9" />
        </AccordionButton>

        <AccordionPanel pt={5} px={4}>
          {/* Advance Time Slider Section */}
          <Flex width={"100%"} flexDirection={"column"} gap={2}>
            <SliderHeader
              t={t}
              title={t("sendConfig.minMaxAdvanceTime")}
              subtitle={t("sendConfig.advanceTimeSubtitle")}
              selectedValue={`${formatAdvanceTimeLabel(
                advanceTimeRange[0],
              )} - ${formatAdvanceTimeLabel(advanceTimeRange[1])}`}
            />
            <Box px={2}>
              <ConfigRangeSlider
                min={minIndex}
                max={maxIndex}
                step={1}
                value={sliderIndexRange}
                marks={advanceTimeSliderMarks}
                formatLabel={formatAdvanceTimeLabel}
                onChange={(val: [number, number]) =>
                  handleAdvanceTimeChange(val)
                }
                onChangeEnd={(val: [number, number]) =>
                  handleAdvanceTimeChangeEnd(val)
                }
                valueInMinutes={advanceTimeRange}
              />
            </Box>
            {values?.maxAdvanceTime &&
              values.maxAdvanceTime > LEGACY_MAX_ADVANCE_TIME_FALLBACK && (
                <Flex gap={2} alignItems="center">
                  <TriangleAlert
                    size={16}
                    color={SEND_CONFIG_THEME.colors.errorText}
                  />
                  <Text
                    color={SEND_CONFIG_THEME.colors.errorText}
                    fontSize={SEND_CONFIG_THEME.fontSize.subtitle}
                  >
                    {t("sendConfig.alertsOver3DaysWarning")}
                  </Text>
                </Flex>
              )}
          </Flex>

          {/* Minimum Event Duration and Receipt Type Section */}
          <Flex gap={8} mt={6}>
            <Flex flexDirection={"column"} width={"100%"} gap={2}>
              <MinimumEventDurationSelect
                t={t}
                value={values?.minimumEventDuration}
                onChange={(selectedValue: number) => {
                  handleChange({
                    target: {
                      name: "minimumEventDuration",
                      value: selectedValue,
                    },
                  });
                }}
              />
            </Flex>
            <Flex width={"100%"} direction={"column"} gap={2}>
              <ReceiptTypeCheckboxes
                t={t}
                value={values?.receiptType}
                onChange={(checkedList: string[]) => {
                  handleChange({
                    target: {
                      name: "receiptType",
                      value: checkedList,
                    },
                  });
                }}
              />
            </Flex>
          </Flex>

          {/* Day Period Slider Section */}
          <Flex width={"100%"} flexDirection={"column"} gap={2} mt={6}>
            <SliderHeader
              t={t}
              title={
                timeRange[0] === 0 && timeRange[1] === 24
                  ? t("sendConfig.eventCanBeCapturedAnytime")
                  : `${t(
                      "sendConfig.eventCanBeCapturedBetween",
                    )} ${formatTimeLabel(timeRange[0])} - ${formatTimeLabel(
                      timeRange[1],
                    )}`
              }
              selectedValue={`${formatTimeLabel(
                timeRange[0],
              )} - ${formatTimeLabel(timeRange[1])}`}
              hideSelectedValue
            />
            <Box px={2}>
              <ConfigRangeSlider
                min={0}
                max={24}
                step={0.5}
                value={timeRange}
                marks={timeRangeSliderMarks}
                formatLabel={formatTimeLabel}
                onChange={(val: [number, number]) => handleTimeRangeChange(val)}
                onChangeEnd={(val: [number, number]) =>
                  handleTimeRangeChangeEnd(val)
                }
              />
            </Box>
            <Text fontSize="13px" color="#64748B" fontStyle="italic">
              *{t("sendConfig.timezoneHelperText")}
            </Text>
          </Flex>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
